// Small DOM helpers + reusable visual components (dial gauge, radar chart).

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Omit<Partial<HTMLElementTagNameMap[K]>, 'style' | 'className'> & { class?: string; style?: string; html?: string; text?: string },
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props ?? {})) {
    if (v == null) continue;
    if (k === 'class') node.className = v as string;
    else if (k === 'html') node.innerHTML = v as string;
    else if (k === 'text') node.textContent = v as string;
    else if (k === 'style' && typeof v === 'string') node.style.cssText = v;
    else (node as unknown as Record<string, unknown>)[k] = v;
  }
  for (const c of children) node.append(c);
  return node;
}

export function svg(tag: string, attrs: Record<string, string> = {}): SVGElement {
  const n = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  return n;
}

// Tuning dial gauge. setNeedle(cents, label) updates needle + readout.
// 指针式表盘(spec FR-1.2):240° 弧形刻度(-50~+50 cent) + 中心 ±5 cent 绿色容差弧线 + 弦名/偏差读数。
export class TuningDial {
  readonly el: SVGElement;
  private needle: SVGElement;
  private label: SVGElement;
  private readout: SVGElement;
  private dirText: SVGElement;

  constructor() {
    const size = 260;
    const cx = size / 2;
    const cy = size / 2 + 24;
    const r = 92;
    // 不写 width/height,让 CSS 控制尺寸(viewBox 等比缩放)
    this.el = svg('svg', { viewBox: `0 0 ${size} ${size}`, class: 'tuner-dial' });
    const defs = svg('defs');
    defs.innerHTML = `<linearGradient id="dialg" x1="0" x2="1"><stop offset="0" stop-color="#ff5470"/><stop offset=".5" stop-color="#3ddc84"/><stop offset="1" stop-color="#4cc9f0"/></linearGradient>`;
    this.el.append(defs);
    // 弧形背景:从 -60° 到 +60° 的 240° 弧,作为色阶底图
    const arc = svg('path', {
      d: describeArc(cx, cy, r, -120, 120),
      stroke: 'url(#dialg)', 'stroke-width': '10', fill: 'none', 'stroke-linecap': 'round', opacity: '.35',
    });
    this.el.append(arc);
    // FR-1.2:中心 ±5 cent 绿色容差弧线(突出显示,稍粗于背景)
    const tolBand = svg('path', {
      d: describeArc(cx, cy, r, angleForCents(-5), angleForCents(5)),
      stroke: '#3ddc84', 'stroke-width': '14', fill: 'none', 'stroke-linecap': 'round', opacity: '.9',
    });
    this.el.append(tolBand);
    // 刻度
    for (let c = -50; c <= 50; c += 10) {
      const ang = angleForCents(c);
      const inner = polar(cx, cy, r - 14, ang);
      const outer = polar(cx, cy, r - 2, ang);
      const tick = svg('line', { x1: String(inner.x), y1: String(inner.y), x2: String(outer.x), y2: String(outer.y), stroke: 'var(--text-faint)', 'stroke-width': c % 50 === 0 ? '2' : '1' });
      this.el.append(tick);
    }
    // 0 标注
    const zero = polar(cx, cy, r + 6, angleForCents(0));
    const zeroTxt = svg('text', { x: String(zero.x), y: String(zero.y - 2), fill: 'var(--text-dim)', 'font-size': '11', 'text-anchor': 'middle' });
    zeroTxt.textContent = '0';
    this.el.append(zeroTxt);
    // 指针
    this.needle = svg('line', { x1: String(cx), y1: String(cy), x2: String(cx), y2: String(cy - r + 18), stroke: 'var(--accent)', 'stroke-width': '3', 'stroke-linecap': 'round' });
    this.needle.style.transformOrigin = `${cx}px ${cy}px`;
    this.needle.style.transition = 'transform .12s ease-out';
    this.el.append(this.needle);
    const hub = svg('circle', { cx: String(cx), cy: String(cy), r: '6', fill: 'var(--accent)' });
    this.el.append(hub);
    // 弦名 / 方向 / 偏差读数
    this.label = svg('text', { x: String(cx), y: String(cy + 34), fill: 'var(--text)', 'font-size': '30', 'font-weight': '700', 'text-anchor': 'middle' });
    this.dirText = svg('text', { x: String(cx), y: String(cy + 54), fill: 'var(--text-dim)', 'font-size': '13', 'text-anchor': 'middle' });
    this.readout = svg('text', { x: String(cx), y: String(cy + 78), fill: 'var(--text-dim)', 'font-size': '14', 'text-anchor': 'middle' });
    this.el.append(this.label, this.dirText, this.readout);
    this.setNeedle(null, '—', 0);
  }

  setNeedle(cents: number | null, label: string, conf: number): void {
    const c = cents ?? 0;
    this.needle.style.transform = `rotate(${angleForCents(c)}deg)`;
    this.label.textContent = label;
    const color = cents == null ? 'var(--text-faint)' : Math.abs(c) < 5 ? 'var(--good)' : 'var(--warn)';
    this.label.setAttribute('fill', color);
    this.dirText.textContent = cents == null ? '等待琴声…' : Math.abs(c) < 5 ? '已准 ✓' : c < 0 ? '偏低 (Flat) ↑ 拉紧' : '偏高 (Sharp) ↓ 放松';
    this.readout.textContent = cents == null ? '' : `${c > 0 ? '+' : ''}${cents} cents · conf ${(conf * 100).toFixed(0)}%`;
  }
}

function angleForCents(cents: number): number {
  // Map -50..+50 cents to -120..+120 degrees.
  const clamped = Math.max(-50, Math.min(50, cents));
  return (clamped / 50) * 120;
}

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polar(cx, cy, r, endAngle);
  const end = polar(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

// Radar chart (canvas) for practice reports (FR-6).
export function radarChart(canvas: HTMLCanvasElement, axes: string[], values: number[], size = 260): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, size, size);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 34;
  const n = axes.length;
  ctx.strokeStyle = 'rgba(124,92,255,.2)';
  ctx.fillStyle = 'rgba(124,92,255,.18)';
  ctx.lineWidth = 1;
  // grid rings
  for (let ring = 1; ring <= 4; ring++) {
    ctx.beginPath();
    const rr = (r * ring) / 4;
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n - Math.PI / 2;
      const x = cx + rr * Math.cos(a);
      const y = cy + rr * Math.sin(a);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  }
  // axes + labels
  ctx.fillStyle = 'var(--text-dim)';
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  for (let i = 0; i < n; i++) {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    const x = cx + (r + 16) * Math.cos(a);
    const y = cy + (r + 16) * Math.sin(a) + 4;
    ctx.fillText(axes[i], x, y);
  }
  // data polygon
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    const rr = (r * Math.max(0, Math.min(100, values[i] ?? 0))) / 100;
    const x = cx + rr * Math.cos(a);
    const y = cy + rr * Math.sin(a);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'var(--accent)';
  ctx.lineWidth = 2;
  ctx.stroke();
}

// Progress ring (canvas) returning the canvas element.
export function scoreRing(score: number, label = '总分', size = 120): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  ctx.scale(dpr, dpr);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 10;
  ctx.lineWidth = 8;
  ctx.strokeStyle = 'var(--border)';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  const color = score >= 80 ? '#3ddc84' : score >= 60 ? '#ffb454' : '#ff5470';
  ctx.strokeStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * Math.max(0, Math.min(100, score))) / 100);
  ctx.stroke();
  ctx.fillStyle = 'var(--text)';
  ctx.font = 'bold 26px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(String(Math.round(score)), cx, cy + 6);
  ctx.fillStyle = 'var(--text-dim)';
  ctx.font = '11px sans-serif';
  ctx.fillText(label, cx, cy + 24);
  return canvas;
}
